import React, { useState, useEffect, useContext } from 'react';
import { api } from './api/api';
import { AuthContext } from '../App';

const TEAM_ROLES = [
    { value: '',            label: 'All teams'   },
    { value: 'FORCE_ONE',   label: 'Force One'   },
    { value: 'BRUTE_FORCE', label: 'Brute Force' },
    { value: 'MIDLAND',     label: 'Midland'     },
    { value: 'SUPERNOVA',   label: 'Supernova'   },
    { value: 'RUSSIA',      label: 'Russia'      },
    { value: 'FRIEND',      label: 'Friend'      },
    { value: 'GUEST',       label: 'Guest'       },
    { value: 'OTHER',       label: 'Other'       },
];

// ── Helpers ────────────────────────────────────────────────────────────────

const val = (v) => v ?? '—';

const PitTimeBadge = ({ time }) => {
    if (time == null) return <span>—</span>;
    const color = time < 6 ? '#16a34a' : time < 8 ? '#d97706' : '#dc2626';
    return <span style={{ fontWeight: 700, color }}>{time.toFixed(3)}s</span>;
};

const WearIndicator = ({ value }) => {
    if (value == null) return <span>—</span>;
    const color = value < 30 ? '#dc2626' : value < 60 ? '#d97706' : '#16a34a';
    return <span style={{ fontWeight: 600, color }}>{value}%</span>;
};

// ── Staff/TD mini-section ─────────────────────────────────────────────────

const StaffTdRow = ({ pit }) => {
    const hasStaff = pit.pitCentreLvl != null || pit.staffExperience != null;
    const hasTd    = pit.tdOverall != null;
    if (!hasStaff && !hasTd) return null;

    const statStyle = { fontSize: 11, color: 'var(--c-text-muted)', marginRight: 12 };
    const valStyle  = { fontWeight: 600, color: 'var(--c-text)' };

    return (
        <div style={{
            padding: '8px 14px',
            background: '#fafafa',
            borderTop: '1px solid var(--c-border-light)',
            display: 'flex', flexWrap: 'wrap', gap: 6,
            fontSize: 11,
        }}>
            {hasStaff && (
                <span style={statStyle}>
                    Pit Centre: <span style={valStyle}>{val(pit.pitCentreLvl)}</span>
                    {' · '}Class: <span style={valStyle}>{val(pit.managerClass)}</span>
                    {' · '}Staff Exp: <span style={valStyle}>{val(pit.staffExperience)}</span>
                    {' · '}Mot: <span style={valStyle}>{val(pit.staffMotivation)}</span>
                    {' · '}Tech: <span style={valStyle}>{val(pit.staffTechKnowledge)}</span>
                    {' · '}Stress: <span style={valStyle}>{val(pit.staffStressResistance)}</span>
                    {' · '}Conc: <span style={valStyle}>{val(pit.staffConcentration)}</span>
                    {' · '}Eff: <span style={valStyle}>{val(pit.staffEfficiency)}</span>
                </span>
            )}
            {hasTd && (
                <span style={statStyle}>
                    TD OVR: <span style={valStyle}>{val(pit.tdOverall)}</span>
                    {' · '}Lead: <span style={valStyle}>{val(pit.tdLeadership)}</span>
                    {' · '}Mech: <span style={valStyle}>{val(pit.tdMechanics)}</span>
                    {' · '}Elec: <span style={valStyle}>{val(pit.tdElectronics)}</span>
                    {' · '}Aero: <span style={valStyle}>{val(pit.tdAerodynamics)}</span>
                    {' · '}Exp: <span style={valStyle}>{val(pit.tdExperience)}</span>
                    {' · '}PitCoord: <span style={valStyle}>{val(pit.tdPitCoordination)}</span>
                    {' · '}Mot: <span style={valStyle}>{val(pit.tdMotivation)}</span>
                </span>
            )}
        </div>
    );
};

// ── Race card ──────────────────────────────────────────────────────────────

const RaceCard = ({ season, raceNumber, pits }) => (
    <div className="fb-card">
        <div className="fb-card-header">
            <span className="fb-card-title">S{season} R{raceNumber}</span>
            <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
                {pits.length} pit{pits.length !== 1 ? 's' : ''}
            </span>
        </div>
        <div className="fb-table-wrap">
            <table className="fb-table fb-table-sm" style={{ whiteSpace: 'nowrap' }}>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Lap</th>
                        <th>Reason</th>
                        <th>Tyres in</th>
                        <th>Fuel in (L)</th>
                        <th>Refill to (L)</th>
                        <th>Added (L)</th>
                        <th>Pit Time</th>
                    </tr>
                </thead>
                <tbody>
                    {pits.map(p => (
                        <tr key={p.id}>
                            <td style={{ fontWeight: 700, color: 'var(--c-text-muted)' }}>{val(p.pitIdx)}</td>
                            <td>{val(p.lap)}</td>
                            <td>{val(p.reason)}</td>
                            <td><WearIndicator value={p.tyreCond} /></td>
                            <td>{val(p.fuelLeft)}</td>
                            <td>{val(p.refilledTo)}</td>
                            <td style={{ fontWeight: 600 }}>{val(p.fuelAdded)}</td>
                            <td><PitTimeBadge time={p.pitTime} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        {/* Show staff/TD from first pit (same for all in race) */}
        <StaffTdRow pit={pits[0]} />
    </div>
);

// ── Main component ─────────────────────────────────────────────────────────

const PitHistory = () => {
    const { userRole } = useContext(AuthContext);
    const isAdmin = userRole === 'ADMIN';

    const [managers,        setManagers]        = useState([]);
    const [selectedManager, setSelectedManager] = useState(null);
    const [history,         setHistory]         = useState([]);
    const [loading,         setLoading]         = useState(true);
    const [detailLoading,   setDetailLoading]   = useState(false);
    const [error,           setError]           = useState(null);
    const [teamRole,        setTeamRole]        = useState('');
    const [detailError,     setDetailError]     = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            setSelectedManager(null);
            setHistory([]);
            try {
                const url = teamRole ? `/driver-history?teamRole=${teamRole}` : '/driver-history';
                const res = await api.get(url);
                setManagers(res.data);
            } catch (err) {
                console.error(err);
                setError('Failed to load managers.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [teamRole]);

    const selectManager = async (manager) => {
        setSelectedManager(manager);
        setDetailLoading(true);
        setDetailError(null);
        try {
            const res = await api.get(`/pit-stops/history/${manager.userId}`);
            setHistory(res.data);
        } catch (err) {
            setHistory([]);
            setDetailError(err.response?.status === 403 ? 'Access denied.' : 'Failed to load data.');
        } finally {
            setDetailLoading(false);
        }
    };

    const handleMobileSelect = (e) => {
        const m = managers.find(m => String(m.userId) === e.target.value);
        if (m) selectManager(m);
    };

    // Group by race
    const byRace = history.reduce((acc, p) => {
        const key = `${p.season}_${p.raceNumber}`;
        if (!acc[key]) acc[key] = { season: p.season, raceNumber: p.raceNumber, pits: [] };
        acc[key].pits.push(p);
        return acc;
    }, {});
    const races = Object.values(byRace);

    const S = {
        sidebar: { width: 220, borderRight: '1px solid var(--c-border)', overflowY: 'auto', flexShrink: 0, background: '#fff' },
        sidebarHeader: { padding: '12px 16px', fontWeight: 700, fontSize: 11, borderBottom: '1px solid var(--c-border)', background: '#fafafa', textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--c-text-muted)' },
        managerItem: (sel) => ({ padding: '10px 16px', cursor: 'pointer', background: sel ? '#fff3f3' : 'transparent', borderLeft: `3px solid ${sel ? 'var(--c-accent)' : 'transparent'}`, fontSize: 13, fontWeight: sel ? 600 : 400, color: 'var(--c-text)', transition: 'background 0.12s' }),
        main: { flex: 1, overflowY: 'auto', padding: 24, background: 'var(--c-bg)', minWidth: 0 },
        placeholder: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--c-text-light)', gap: 12 },
    };

    return (
        <div className="ra-page">
            <div style={S.sidebar} className="ra-desktop-sidebar">
                <div style={S.sidebarHeader}>Managers</div>
                {isAdmin && (
                    <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--c-border)' }}>
                        <select value={teamRole} onChange={e => setTeamRole(e.target.value)}
                            style={{ width: '100%', padding: '5px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--c-border)', fontSize: 12, background: '#fff', color: 'var(--c-text)', cursor: 'pointer' }}>
                            {TEAM_ROLES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                    </div>
                )}
                {loading && <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>Loading…</div>}
                {error   && <div style={{ padding: 16, color: 'var(--c-error)', fontSize: 13 }}>{error}</div>}
                {!loading && !error && managers.length === 0 && <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>No data.</div>}
                {managers.map(m => (
                    <div key={m.userId} style={S.managerItem(selectedManager?.userId === m.userId)} onClick={() => selectManager(m)}>
                        {m.managerName}
                    </div>
                ))}
            </div>

            <div style={S.main} className="ra-main">
                <div className="ra-mobile-selector">
                    <select className="fb-select" style={{ width: '100%' }} value={selectedManager?.userId ?? ''} onChange={handleMobileSelect} disabled={loading}>
                        <option value="">{loading ? 'Loading…' : '— Select a manager —'}</option>
                        {managers.map(m => <option key={m.userId} value={m.userId}>{m.managerName}</option>)}
                    </select>
                </div>

                {!selectedManager && (
                    <div style={S.placeholder} className="ra-desktop-placeholder">
                        <span style={{ fontSize: 44 }}>🔧</span>
                        <span style={{ fontSize: 14 }}>Select a manager to view pit stop history</span>
                    </div>
                )}

                {selectedManager && detailLoading && (
                    <div style={{ color: 'var(--c-text-muted)', marginTop: 48, textAlign: 'center', fontSize: 14 }}>Loading pit stops…</div>
                )}

                {selectedManager && !detailLoading && (
                    <>
                        <div className="fb-page-header">
                            <div className="fb-page-title">{selectedManager.managerName}</div>
                            <div className="fb-page-sub">{history.length} pit stop{history.length !== 1 ? 's' : ''} across {races.length} race{races.length !== 1 ? 's' : ''}</div>
                        </div>
                        {detailError
                            ? <div style={{ color: 'var(--c-error)', fontSize: 14 }}>{detailError}</div>
                            : races.length === 0
                                ? <div style={{ color: 'var(--c-text-muted)', fontSize: 14 }}>No pit stop data available.</div>
                                : races.map(r => <RaceCard key={`${r.season}_${r.raceNumber}`} season={r.season} raceNumber={r.raceNumber} pits={r.pits} />)
                        }
                    </>
                )}
            </div>
        </div>
    );
};

export default PitHistory;
